/* eslint-disable no-unused-vars */
import { memo, useState, useEffect } from 'react';
import { Palette } from 'config';
import * as S from './GeoBudget.styled';
import { DropDownSelect, Bid, Buttons } from 'components';
import { ClearSearchInput } from 'assets/icons';

export const GeoBudget = memo(
  ({
    uniqueId,
    countriesBudgetItems,
    onUpdateGeoBudgetItems,
    onChangeMonthlyBudget,
    allCountries,
  }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [currentBudget, setCurrentBudget] = useState('');
    const [geoBudgetItems, setGeoBudgetItems] = useState(countriesBudgetItems);

    function addGeoBudgetItem() {
      const newItem = {
        country_code: selectedCountry.country_code,
        country_name: selectedCountry.country_name,
        budget: currentBudget,
      };
      setGeoBudgetItems((items) => [...items, newItem]);

      onUpdateGeoBudgetItems(uniqueId, [...geoBudgetItems, newItem]);

      setSelectedCountry(null);
      setCurrentBudget('');
      setSearchQuery('');
    }

    function handleCurrentBudget(e) {
      let newValue = e.target.value;

      if (/[^0]/.test(newValue)) {
        newValue = newValue.replace(/^0+/, '');
      }

      if (newValue.toString().length > 6) return;
      setCurrentBudget(newValue);
    }

    function deleteGeoBudgetItem(item) {
      const newItems = geoBudgetItems.filter(
        (country) => country.country_code !== item.country_code,
      );
      if (geoBudgetItems.length && !newItems.length) {
        onChangeMonthlyBudget(uniqueId, 0);
      }
      setGeoBudgetItems(newItems);
      onUpdateGeoBudgetItems(uniqueId, newItems);
    }

    function handleSearch(e) {
      if (selectedCountry) {
        setSelectedCountry(null);
      }
      setSearchQuery(e.target.value);
    }

    function resetDropDownInput() {
      setSelectedCountry(null);
      setSearchQuery('');
    }

    function handleSelect(country) {
      setSearchQuery('');
      setSelectedCountry(country);
    }

    return (
      <S.GeoBudgetWrap>
        <DropDownSelect
          width={254}
          placeholder={allCountries?.length === geoBudgetItems?.length ? '—' : 'Search Country'}
          maxHeight={170}
          search={true}
          searchValue={searchQuery}
          onSearch={handleSearch}
          activeElem={
            selectedCountry && `${selectedCountry.country_code} – ${selectedCountry.country_name}`
          }
          onResetValue={resetDropDownInput}
        >
          {allCountries?.length > 0 &&
            geoBudgetItems?.length < allCountries?.length &&
            searchItems(allCountries, searchQuery).map((country, ind) => {
              return (
                <li
                  key={country.country_code + ind}
                  style={
                    isCountrySelected(country, geoBudgetItems)
                      ? { display: 'none' }
                      : { display: 'block' } // for ellipsis
                  }
                  className={selectedCountry?.country_code === country.country_code ? 'active' : ''}
                  onClick={() => handleSelect(country)}
                >
                  {`${country.country_code} – ${country.country_name}`}
                </li>
              );
            })}
        </DropDownSelect>
        <S.BidWrap>
          <Bid.Big
            value={currentBudget ?? ''}
            error={Boolean(currentBudget) && currentBudget < 100}
            errorMesage='Minimum Budget 100$'
            width={60}
            style={
              allCountries?.length === geoBudgetItems?.length ||
              !allCountries ||
              allCountries?.length === 0
                ? { opacity: '0.5', pointerEvents: 'none' }
                : {}
            }
            onChange={handleCurrentBudget}
          />
        </S.BidWrap>
        <Buttons.SecondaryNormal
          id='add_geobudget_item'
          style={{ width: '50px' }}
          isDisabled={!+currentBudget || !selectedCountry || (currentBudget && currentBudget < 100)}
          onClick={addGeoBudgetItem}
        >
          Add
        </Buttons.SecondaryNormal>
        <S.GeoBudgetItemsWrap>
          {geoBudgetItems &&
            geoBudgetItems.map((item, ind) => {
              return (
                <GeoBudgetItem
                  key={item.country_code + ind}
                  item={item}
                  onDelete={() => deleteGeoBudgetItem(item)}
                />
              );
            })}
        </S.GeoBudgetItemsWrap>
      </S.GeoBudgetWrap>
    );
  },
);

function searchItems(searchedItems, searchValue) {
  if (!searchValue) return searchedItems;
  const pattern = searchValue.toLowerCase().trim();

  return searchedItems.filter((item) => {
    if (item.country_name?.toLowerCase().includes(pattern)) return item;
    if (item.country_code?.toLowerCase().includes(pattern)) return item;
  });
}

function isCountrySelected(country, allSelectedCountries) {
  if (allSelectedCountries?.length > 0) {
    return allSelectedCountries.some((item) => item.country_code === country.country_code);
  } else {
    return false;
  }
}

const GeoBudgetItem = ({ item, onDelete = () => {} }) => {
  return (
    <S.GeoBudgetItem>
      <span>{`${item.country_code} – ${item.country_name} ${item.budget}$`}</span>
      <S.IconWrapper onClick={onDelete}>
        <ClearSearchInput />
      </S.IconWrapper>
    </S.GeoBudgetItem>
  );
};

GeoBudget.displayName = 'GeoBudget';
